import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Invoice } from '@/graphql/graphql';
import { useSubscription } from '@/hooks/useSubscription';
import dayjs from 'dayjs';
import { Download, ExternalLink, FileText } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100);
};

const getStatusBadgeVariant = (status: string) => {
  switch (status.toLowerCase()) {
    case 'paid':
      return 'success';
    case 'open':
    case 'draft':
      return 'warning';
    case 'uncollectible':
    case 'void':
      return 'destructive';
    default:
      return 'secondary';
  }
};

export const BillingHistory = () => {
  const { details, loading, error, fetchSubscriptionDetails } = useSubscription();
  const { t } = useTranslation('subscription');

  const getStatusLabel = (status: string): string => {
    const key = status.toLowerCase();
    const labels: Record<string, string> = {
      paid: t('billingHistory.paid'),
      open: t('billingHistory.open'),
      draft: t('billingHistory.draft'),
      uncollectible: t('billingHistory.uncollectible'),
      void: t('billingHistory.void'),
    };
    return labels[key] || status;
  };

  useEffect(() => {
    fetchSubscriptionDetails();
  }, [fetchSubscriptionDetails]);

  if (loading && !details) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('billingHistory.title')}</CardTitle>
          <CardDescription>{t('billingHistory.loading')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('billingHistory.title')}</CardTitle>
          <CardDescription className='text-red-500'>{error}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const invoices = details?.invoices || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('billingHistory.title')}</CardTitle>
        <CardDescription>{t('billingHistory.description')}</CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        {invoices.length === 0 ? (
          <div className='text-center py-6 text-slate-400 dark:text-stone-500'>
            <FileText className='h-8 w-8 mx-auto mb-2 opacity-50' />
            <p className='text-sm'>{t('billingHistory.noInvoices')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('billingHistory.invoice')}</TableHead>
                <TableHead>{t('billingHistory.date')}</TableHead>
                <TableHead>{t('billingHistory.amount')}</TableHead>
                <TableHead>{t('billingHistory.status')}</TableHead>
                <TableHead className='text-right'>{t('billingHistory.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice: Invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell className='font-medium text-sm'>{invoice.number || invoice.id.slice(0, 8)}</TableCell>
                  <TableCell className='text-sm'>{dayjs(invoice.created).format('MMM D, YYYY')}</TableCell>
                  <TableCell className='text-sm'>{formatCurrency(invoice.amountDue, invoice.currency)}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(invoice.status)}>{getStatusLabel(invoice.status)}</Badge>
                  </TableCell>
                  <TableCell className='text-right'>
                    <div className='flex items-center justify-end gap-1'>
                      {invoice.hostedInvoiceUrl && (
                        <Button variant='ghost' size='sm' className='h-7 px-2' asChild>
                          <a href={invoice.hostedInvoiceUrl} target='_blank' rel='noopener noreferrer'>
                            <ExternalLink className='h-3.5 w-3.5' />
                          </a>
                        </Button>
                      )}
                      {invoice.pdfUrl && (
                        <Button variant='ghost' size='sm' className='h-7 px-2' asChild>
                          <a href={invoice.pdfUrl} target='_blank' rel='noopener noreferrer'>
                            <Download className='h-3.5 w-3.5' />
                          </a>
                        </Button>
                      )}
                      {invoice.taxInvoiceUrl && (
                        <Button variant='ghost' size='sm' className='h-7 px-2' asChild>
                          <a
                            href={invoice.taxInvoiceUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            title={t('billingHistory.taxInvoice')}
                          >
                            <FileText className='h-3.5 w-3.5' />
                          </a>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Next invoice preview */}
        {details?.nextInvoicePreview && (
          <div className='rounded-md bg-slate-50 dark:bg-stone-900 p-3'>
            <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide mb-2'>
              {t('billingHistory.upcomingInvoice')}
            </p>
            <div className='space-y-1.5 text-sm'>
              <div className='flex justify-between text-xs text-slate-500 dark:text-stone-400'>
                <span>{t('billingHistory.period')}</span>
                <span>
                  {dayjs(details.nextInvoicePreview.periodStart).format('MMM D')} -{' '}
                  {dayjs(details.nextInvoicePreview.periodEnd).format('MMM D, YYYY')}
                </span>
              </div>
              {details.nextInvoicePreview.lines.map((line, index) => (
                <div key={index} className='flex justify-between text-xs'>
                  <span className='text-slate-600 dark:text-stone-400'>
                    {line.description}
                    {line.quantity && line.quantity > 1 && ` (\u00d7${line.quantity})`}
                  </span>
                  <span className='text-slate-900 dark:text-white'>
                    {formatCurrency(line.amount, details.nextInvoicePreview.currency)}
                  </span>
                </div>
              ))}
              <div className='flex justify-between pt-1.5 border-t border-slate-200 dark:border-stone-700 text-xs font-medium'>
                <span className='text-slate-900 dark:text-white'>{t('billingHistory.total')}</span>
                <span className='text-slate-900 dark:text-white'>
                  {formatCurrency(details.nextInvoicePreview.amountDue, details.nextInvoicePreview.currency)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Active discount */}
        {details?.discount && (
          <div className='text-sm rounded-md bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 px-3 py-2'>
            <span className='text-green-800 dark:text-green-200'>
              {details.discount.name || t('billingHistory.promotionalDiscount')}
              {details.discount.percentOff && ` \u2014 ${t('billingHistory.percentOff', { percent: details.discount.percentOff })}`}
              {details.discount.amountOff && ` \u2014 ${t('billingHistory.amountOff', { amount: formatCurrency(details.discount.amountOff, details.nextInvoicePreview?.currency || 'eur') })}`}
              {details.discount.expiresAt && ` (${t('billingHistory.expires', { date: dayjs(details.discount.expiresAt).format('MMM D, YYYY') })})`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
